/**
 * Governança do benefício anual estimado.
 *
 * annual_cost_saved é R$/ano estimado do Kaizen.
 * Não é caixa realizado, ganho do mês nem validação financeira.
 * A sugestão por lead time não grava esse campo.
 */

export const SUGGESTED_HOURLY_RATE_BRL = 50;
export const SUGGESTED_WORKING_DAYS_PER_YEAR = 250;

export const KAIZEN_OPERATIONAL_STATUSES = [
  'planejado',
  'em_implementacao',
  'implementado',
  'em_monitoramento',
  'sustentado',
  'regredido',
] as const;

export type KaizenOperationalStatus = (typeof KAIZEN_OPERATIONAL_STATUSES)[number];

/**
 * Onde o valor anual entra no resumo. Status operacional não é prova de resultado.
 * `planned` é o grupo potencial: planejado, em implementação e implementado.
 * `active` é somente monitoramento e sustentação. `at_risk` é regressão.
 */
export type BenefitInclusion = 'active' | 'planned' | 'at_risk' | 'unrecognized';

const OPERATIONAL_STATUS_LABEL: Record<KaizenOperationalStatus, string> = {
  planejado: 'Planejado',
  em_implementacao: 'Em implementação',
  implementado: 'Implementado',
  em_monitoramento: 'Em monitoramento',
  sustentado: 'Sustentado',
  regredido: 'Regredido',
};

const POTENTIAL_STATUSES = new Set<KaizenOperationalStatus>([
  'planejado',
  'em_implementacao',
  'implementado',
]);

const ACTIVE_STATUSES = new Set<KaizenOperationalStatus>([
  'em_monitoramento',
  'sustentado',
]);

const BENEFIT_NOTE: Record<KaizenOperationalStatus, string> = {
  planejado: 'Potencial',
  em_implementacao: 'Potencial — em implementação',
  implementado: 'Aguardando validação',
  em_monitoramento: 'Benefício ativo em monitoramento',
  sustentado: 'Benefício sustentado',
  regredido: 'Benefício em risco',
};

export function isKnownKaizenStatus(status: string): status is KaizenOperationalStatus {
  return (KAIZEN_OPERATIONAL_STATUSES as readonly string[]).includes(status);
}

export function benefitInclusion(status: string): BenefitInclusion {
  if (!isKnownKaizenStatus(status)) return 'unrecognized';
  if (status === 'regredido') return 'at_risk';
  if (ACTIVE_STATUSES.has(status)) return 'active';
  if (POTENTIAL_STATUSES.has(status)) return 'planned';
  return 'unrecognized';
}

export function operationalStatusLabel(status: string): string {
  if (!isKnownKaizenStatus(status)) return 'Situação não reconhecida';
  return OPERATIONAL_STATUS_LABEL[status];
}

export function inclusionNote(inclusion: BenefitInclusion, status?: string): string {
  if (status && isKnownKaizenStatus(status)) return BENEFIT_NOTE[status];
  if (inclusion === 'active') return 'Benefício ativo';
  if (inclusion === 'planned') return 'Potencial — não incluído no ativo';
  if (inclusion === 'at_risk') return 'Benefício em risco';
  return 'Situação não reconhecida — não incluído';
}

export interface SuggestedAnnualSavings {
  annualTimeSavedHours: number;
  suggestedAnnualCost: number;
  hourlyRateBrl: number;
  workingDaysPerYear: number;
}

/**
 * Estimativa antiga a partir de lead time. Não é benefício oficial.
 * Não substitui current por target e não pode ser gravada em annual_cost_saved.
 * Permanece só para o contrato já testado; o Monitor não a usa na leitura.
 */
export function calculateSuggestedAnnualSavings(
  baselineLeadTimeMinutes: number,
  observedLeadTimeMinutes: number,
): SuggestedAnnualSavings {
  const annualTimeSavedHours = ((baselineLeadTimeMinutes - observedLeadTimeMinutes) / 60) * SUGGESTED_WORKING_DAYS_PER_YEAR;
  return {
    annualTimeSavedHours,
    suggestedAnnualCost: annualTimeSavedHours * SUGGESTED_HOURLY_RATE_BRL,
    hourlyRateBrl: SUGGESTED_HOURLY_RATE_BRL,
    workingDaysPerYear: SUGGESTED_WORKING_DAYS_PER_YEAR,
  };
}

/**
 * Atualização operacional não copia a sugestão para o benefício oficial.
 * Null permanece null. Zero gravado permanece zero.
 */
export function formatAnnualBenefit(value: number | null): string {
  if (value == null) return '—';
  const formatted = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  return `${formatted}/ano`;
}

export function officialAnnualBenefitAfterOperationalUpdate(
  officialAnnualCostSaved: number | null | undefined,
): number | null {
  return officialAnnualCostSaved == null ? null : officialAnnualCostSaved;
}

export interface CanonicalAnnualFinance {
  investment: number | null;
  annualBenefit: number | null;
  roiPercent: number | null;
  paybackMonths: number | null;
}

function finiteAmount(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * ROI e payback do Monitor de Ganhos, derivados na leitura.
 * roi_percentage e payback_months gravados ficam fora desta conta.
 * Target, current e validação de resultado também ficam fora.
 *
 * ROI anual = ((B - I) / I) * 100 quando I > 0 e B é finito.
 * I ausente, I = 0 ou B ausente deixam o ROI sem valor.
 * B = 0 com I > 0 produz -100%.
 *
 * Payback em meses = (I / B) * 12 quando B > 0 e I é finito e >= 0.
 * I = 0 com B > 0 produz 0 meses. B ausente ou <= 0, ou I ausente, deixam o payback sem valor.
 */
export function canonicalAnnualFinance(
  annualBenefit: number | null | undefined,
  investment: number | null | undefined,
): CanonicalAnnualFinance {
  const benefit = finiteAmount(annualBenefit);
  const invested = finiteAmount(investment);
  const roiPercent = invested != null && invested > 0 && benefit != null
    ? ((benefit - invested) / invested) * 100
    : null;
  const paybackMonths = benefit != null && benefit > 0 && invested != null && invested >= 0
    ? (invested / benefit) * 12
    : null;
  return { investment: invested, annualBenefit: benefit, roiPercent, paybackMonths };
}

export function annualBenefitLabel(inclusion: BenefitInclusion): string {
  if (inclusion === 'active') return 'Benefício anual ativo estimado';
  if (inclusion === 'at_risk') return 'Benefício anual em risco';
  return 'Benefício anual estimado';
}

export function benefitClassLabel(inclusion: BenefitInclusion): string {
  if (inclusion === 'active') return 'Ativo';
  if (inclusion === 'planned') return 'Potencial';
  if (inclusion === 'at_risk') return 'Em risco';
  return 'Situação não reconhecida';
}

export function formatCanonicalPercent(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const rounded = Math.round(value * 100) / 100;
  const text = Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
  return `${text}%`;
}

export function formatPaybackMonths(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const rounded = Math.round(value * 100) / 100;
  const text = Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
  return `${text} ${rounded === 1 ? 'mês' : 'meses'}`;
}
